import { CaretRight, Check as CheckIcon, X } from '@phosphor-icons/react';
import {
  type ReactNode,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react';
import { createPortal } from 'react-dom';
import { isMobileNow, pushBackHandler, useIsMobile } from '@/core/platform';
import { Button, cx, IconButton, Input } from './components';
import { isDragging } from './dnd';

// Dialogs, bottom sheets, menus and toasts. On phones dialogs become bottom sheets and
// menus become action sheets with big rows; Android Back closes the top one.

// ------------------------------------------------------------------ Sheet / Dialog

export interface SheetProps {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  /** desktop width */
  size?: 'normal' | 'wide' | 'full';
  /** phones: take the whole height */
  fullHeight?: boolean;
  headerActions?: ReactNode;
  className?: string;
}

export function Sheet({
  open,
  onClose,
  title,
  children,
  footer,
  size = 'normal',
  fullHeight,
  headerActions,
  className,
}: SheetProps) {
  const mobile = useIsMobile();
  const sheetRef = useRef<HTMLDivElement>(null);
  const drag = useRef<{ y: number; dy: number } | null>(null);

  useEffect(() => {
    if (!open) return;
    const pop = pushBackHandler(() => {
      onClose();
      return true;
    });
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => {
      pop();
      window.removeEventListener('keydown', onKey, true);
    };
  }, [open, onClose]);

  if (!open) return null;

  // Swipe the grab handle down to dismiss.
  const onTouchStart = (e: React.TouchEvent) => {
    drag.current = { y: e.touches[0]!.clientY, dy: 0 };
  };
  const onTouchMove = (e: React.TouchEvent) => {
    if (!drag.current || !sheetRef.current) return;
    const dy = Math.max(0, e.touches[0]!.clientY - drag.current.y);
    drag.current.dy = dy;
    sheetRef.current.style.transform = `translateY(${dy}px)`;
  };
  const onTouchEnd = () => {
    if (!drag.current || !sheetRef.current) return;
    if (drag.current.dy > 90) onClose();
    else sheetRef.current.style.transform = '';
    drag.current = null;
  };

  const head = (title || headerActions) && (
    <div className="e-dialog__head">
      <div className="e-dialog__title">{title}</div>
      {headerActions}
      {!mobile && (
        <IconButton label="Close" size="sm" onClick={onClose}>
          <X size={18} />
        </IconButton>
      )}
    </div>
  );

  return createPortal(
    <>
      <div className="e-overlay" onClick={onClose} />
      {mobile ? (
        <div
          ref={sheetRef}
          className={cx('e-sheet', fullHeight && 'e-sheet--full', className)}
          role="dialog"
        >
          <div onTouchStart={onTouchStart} onTouchMove={onTouchMove} onTouchEnd={onTouchEnd}>
            <div className="e-sheet__grab" />
            {head}
          </div>
          <div className="e-dialog__body">{children}</div>
          {footer && <div className="e-dialog__foot">{footer}</div>}
        </div>
      ) : (
        <div
          className={cx('e-dialog', size !== 'normal' && `e-dialog--${size}`, className)}
          role="dialog"
        >
          {head}
          <div className="e-dialog__body">{children}</div>
          {footer && <div className="e-dialog__foot">{footer}</div>}
        </div>
      )}
    </>,
    document.body,
  );
}

// ------------------------------------------------------------------ imperative overlay host

export interface MenuItem {
  label?: string;
  icon?: ReactNode;
  onClick?: () => void;
  danger?: boolean;
  checked?: boolean;
  shortcut?: string;
  disabled?: boolean;
  /** section label row */
  heading?: string;
  divider?: boolean;
  /** opens a second menu in the same place (keeps long menus short) */
  submenu?: MenuItem[];
}

type Anchor = HTMLElement | { x: number; y: number };

interface MenuState {
  id: number;
  anchor: Anchor;
  items: MenuItem[];
  title?: string;
}

interface DialogState {
  id: number;
  kind: 'confirm' | 'prompt' | 'alert';
  title: string;
  message?: ReactNode;
  confirmText?: string;
  cancelText?: string;
  danger?: boolean;
  label?: string;
  defaultValue?: string;
  placeholder?: string;
  inputType?: string;
  inputMode?: 'numeric';
  resolve: (v: unknown) => void;
}

interface ToastState {
  id: number;
  message: ReactNode;
  kind: 'info' | 'error';
  action?: { label: string; onClick: () => void };
}

let seq = 0;
let menus: MenuState[] = [];
let dialogs: DialogState[] = [];
let toasts: ToastState[] = [];
const listeners = new Set<() => void>();
let snapshot = { menus, dialogs, toasts };
function emit() {
  snapshot = { menus, dialogs, toasts };
  for (const l of listeners) l();
}

/** Open a menu at an element or a point (context menu). Becomes an action sheet on phones. */
export function openMenu(anchor: Anchor, items: MenuItem[], title?: string) {
  menus = [{ id: ++seq, anchor, items, title }];
  emit();
}

export function closeMenu() {
  menus = [];
  emit();
}

export function confirmDialog(opts: {
  title: string;
  message?: ReactNode;
  confirmText?: string;
  cancelText?: string;
  danger?: boolean;
}): Promise<boolean> {
  return new Promise((resolve) => {
    dialogs = [
      ...dialogs,
      { id: ++seq, kind: 'confirm', ...opts, resolve: resolve as (v: unknown) => void },
    ];
    emit();
  });
}

export function promptDialog(opts: {
  title: string;
  message?: ReactNode;
  label?: string;
  defaultValue?: string;
  placeholder?: string;
  confirmText?: string;
  inputType?: 'text' | 'password' | 'number' | 'url' | 'email' | 'date';
  /** 'numeric': phones show the number pad (PINs) */
  inputMode?: 'numeric';
}): Promise<string | null> {
  return new Promise((resolve) => {
    dialogs = [
      ...dialogs,
      { id: ++seq, kind: 'prompt', ...opts, resolve: resolve as (v: unknown) => void },
    ];
    emit();
  });
}

export function alertDialog(title: string, message?: ReactNode): Promise<void> {
  return new Promise((resolve) => {
    dialogs = [
      ...dialogs,
      { id: ++seq, kind: 'alert', title, message, resolve: resolve as (v: unknown) => void },
    ];
    emit();
  });
}

export function toast(
  message: ReactNode,
  opts: {
    kind?: 'info' | 'error';
    action?: { label: string; onClick: () => void };
    ms?: number;
  } = {},
) {
  const t: ToastState = { id: ++seq, message, kind: opts.kind ?? 'info', action: opts.action };
  // The same message again (pull to refresh twice…) replaces the one showing, never stacks.
  const same = (x: ToastState) =>
    typeof message === 'string' && x.message === message && x.kind === t.kind && !x.action;
  toasts = [...toasts.filter((x) => !same(x)).slice(-2), t];
  emit();
  setTimeout(
    () => {
      toasts = toasts.filter((x) => x.id !== t.id);
      emit();
    },
    opts.ms ?? (opts.action ? 6000 : 3200),
  );
}

export function toastError(err: unknown) {
  toast((err as Error)?.message ?? String(err), { kind: 'error' });
}

function useOverlays() {
  return useSyncExternalStore(
    (cb) => {
      listeners.add(cb);
      return () => listeners.delete(cb);
    },
    () => snapshot,
  );
}

export function OverlayHost() {
  const { menus: m, dialogs: d, toasts: t } = useOverlays();
  return (
    <>
      {m.map((menu) => (
        <MenuView key={menu.id} menu={menu} />
      ))}
      {d.map((dlg) => (
        <DialogView key={dlg.id} dlg={dlg} />
      ))}
      {t.length > 0 &&
        createPortal(
          <div className="e-toasts">
            {t.map((x) => (
              <div
                key={x.id}
                className={cx('e-toast', x.kind === 'error' && 'e-toast--error')}
                role="status"
              >
                <span className="grow">{x.message}</span>
                {x.action && (
                  <Button
                    size="sm"
                    variant="soft"
                    onClick={() => {
                      x.action!.onClick();
                      toasts = toasts.filter((y) => y.id !== x.id);
                      emit();
                    }}
                  >
                    {x.action.label}
                  </Button>
                )}
              </div>
            ))}
          </div>,
          document.body,
        )}
    </>
  );
}

function MenuItems({
  items,
  onDone,
  onSub,
  onHover,
}: {
  items: MenuItem[];
  onDone: () => void;
  onSub: (it: MenuItem, el: HTMLElement) => void;
  /** desktop: hovering a row (submenu rows open their flyout) */
  onHover?: (it: MenuItem, el: HTMLElement) => void;
}) {
  return (
    <>
      {items.map((it, i) => {
        if (it.divider) return <div key={i} className="e-divider" />;
        if (it.heading)
          return (
            <div key={i} className="e-menu__label">
              {it.heading}
            </div>
          );
        return (
          <button
            key={i}
            type="button"
            role="menuitem"
            disabled={it.disabled}
            aria-checked={it.checked}
            className={cx('e-menu__item', it.danger && 'e-menu__item--danger')}
            style={it.disabled ? { opacity: 0.45 } : undefined}
            onMouseEnter={onHover ? (e) => onHover(it, e.currentTarget) : undefined}
            onClick={(e) => {
              if (it.submenu) return onSub(it, e.currentTarget);
              onDone();
              it.onClick?.();
            }}
          >
            {it.icon}
            <span className="grow">{it.label}</span>
            {it.checked !== undefined && (
              <CheckIcon
                size={16}
                className="e-menu__check"
                style={{ opacity: it.checked ? 1 : 0 }}
              />
            )}
            {it.shortcut && <span className="xsmall muted">{it.shortcut}</span>}
            {it.submenu && <CaretRight size={14} />}
          </button>
        );
      })}
    </>
  );
}

function MenuView({ menu }: { menu: MenuState }) {
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ left: number; top: number } | null>(null);
  const mobile = isMobileNow();
  const close = useCallback(() => closeMenu(), []);
  const openSub = (it: MenuItem) => openMenu(menu.anchor, it.submenu ?? [], it.label);
  // Desktop: submenus fly out beside their row on hover instead of replacing the menu.
  const [fly, setFly] = useState<{ item: MenuItem; rect: DOMRect } | null>(null);
  const flyRef = useRef<HTMLDivElement>(null);
  const [flyPos, setFlyPos] = useState<{ left: number; top: number } | null>(null);
  const hoverTimer = useRef<number | undefined>(undefined);
  const onHover = (it: MenuItem, el: HTMLElement) => {
    window.clearTimeout(hoverTimer.current);
    const rect = el.getBoundingClientRect();
    hoverTimer.current = window.setTimeout(
      () => setFly(it.submenu ? { item: it, rect } : null),
      it.submenu ? 0 : 120,
    );
  };
  useEffect(() => () => window.clearTimeout(hoverTimer.current), []);
  useLayoutEffect(() => {
    if (!fly || !flyRef.current) return setFlyPos(null);
    const r = flyRef.current.getBoundingClientRect();
    let left = fly.rect.right + 4;
    if (left + r.width > innerWidth - 8) left = fly.rect.left - r.width - 4;
    let top = fly.rect.top - 6;
    if (top + r.height > innerHeight - 8) top = innerHeight - r.height - 8;
    setFlyPos({ left: Math.max(8, left), top: Math.max(8, top) });
  }, [fly]);

  useEffect(() => {
    const pop = pushBackHandler(() => {
      close();
      return true;
    });
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && close();
    window.addEventListener('keydown', onKey);
    return () => {
      pop();
      window.removeEventListener('keydown', onKey);
    };
  }, [close]);

  useLayoutEffect(() => {
    if (mobile || !ref.current) return;
    const r = ref.current.getBoundingClientRect();
    let left: number;
    let top: number;
    if (menu.anchor instanceof HTMLElement) {
      const a = menu.anchor.getBoundingClientRect();
      left = a.right - r.width;
      if (left < 8) left = a.left;
      top = a.bottom + 4;
      // Below the button, else above it, else as high as needed so nothing is cut off.
      if (top + r.height > innerHeight - 8) top = a.top - r.height - 4;
      if (top < 8) top = innerHeight - 8 - r.height;
    } else {
      left = menu.anchor.x;
      top = menu.anchor.y;
      if (left + r.width > innerWidth - 8) left = innerWidth - r.width - 8;
      if (top + r.height > innerHeight - 8) top = innerHeight - r.height - 8;
    }
    setPos({ left: Math.max(8, left), top: Math.max(8, top) });
  }, [menu, mobile]);

  if (mobile) {
    // Destructive actions stay pinned under the list, so Delete never needs scrolling.
    const danger = menu.items.filter((it) => it.danger);
    const rest = menu.items.filter((it) => !it.danger);
    while (rest.length && rest[rest.length - 1]!.divider) rest.pop();
    return (
      <Sheet
        open
        onClose={close}
        title={menu.title}
        className="e-sheet--menu"
        footer={
          danger.length ? <MenuItems items={danger} onDone={close} onSub={openSub} /> : undefined
        }
      >
        <div style={{ paddingBottom: 8 }}>
          <MenuItems items={rest} onDone={close} onSub={openSub} />
        </div>
      </Sheet>
    );
  }
  return createPortal(
    <>
      <div
        style={{ position: 'fixed', inset: 0, zIndex: 119 }}
        onClick={close}
        onContextMenu={(e) => {
          e.preventDefault();
          close();
        }}
      />
      <div
        ref={ref}
        className="e-menu"
        role="menu"
        style={pos ? { left: pos.left, top: pos.top } : { left: -9999, top: -9999 }}
      >
        <MenuItems
          items={menu.items}
          onDone={close}
          onSub={(it, el) => setFly({ item: it, rect: el.getBoundingClientRect() })}
          onHover={onHover}
        />
      </div>
      {fly && (
        <div
          ref={flyRef}
          className="e-menu"
          role="menu"
          aria-label={fly.item.label}
          style={flyPos ? { left: flyPos.left, top: flyPos.top } : { left: -9999, top: -9999 }}
          onMouseEnter={() => window.clearTimeout(hoverTimer.current)}
        >
          <MenuItems items={fly.item.submenu ?? []} onDone={close} onSub={openSub} />
        </div>
      )}
    </>,
    document.body,
  );
}

function DialogView({ dlg }: { dlg: DialogState }) {
  const [value, setValue] = useState(dlg.defaultValue ?? '');
  const done = (v: unknown) => {
    dialogs = dialogs.filter((x) => x.id !== dlg.id);
    emit();
    dlg.resolve(v);
  };
  const cancel = () =>
    done(dlg.kind === 'confirm' ? false : dlg.kind === 'prompt' ? null : undefined);
  const ok = () => done(dlg.kind === 'confirm' ? true : dlg.kind === 'prompt' ? value : undefined);
  return (
    <Sheet
      open
      onClose={cancel}
      title={dlg.title}
      footer={
        <>
          {dlg.kind !== 'alert' && (
            <Button variant="ghost" onClick={cancel}>
              {dlg.cancelText ?? 'Cancel'}
            </Button>
          )}
          <Button variant={dlg.danger ? 'danger' : 'primary'} onClick={ok}>
            {dlg.confirmText ?? 'OK'}
          </Button>
        </>
      }
    >
      <div className="col gap-12">
        {dlg.message && <div className="dim">{dlg.message}</div>}
        {dlg.kind === 'prompt' && (
          <Input
            autoFocus
            type={dlg.inputType ?? 'text'}
            inputMode={dlg.inputMode}
            enterKeyHint="go"
            autoComplete={dlg.inputMode === 'numeric' ? 'off' : undefined}
            value={value}
            placeholder={dlg.placeholder}
            aria-label={dlg.label ?? dlg.title}
            onChange={(e) =>
              setValue(
                dlg.inputMode === 'numeric' ? e.target.value.replace(/\D/g, '') : e.target.value,
              )
            }
            onKeyDown={(e) => e.key === 'Enter' && ok()}
          />
        )}
      </div>
    </Sheet>
  );
}

/** Attach to any element: right-click on desktop, long-press on touch. */
export function useContextMenu(getItems: () => MenuItem[], title?: string) {
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const start = useRef<{ x: number; y: number } | null>(null);
  return {
    onContextMenu: (e: React.MouseEvent) => {
      e.preventDefault();
      openMenu({ x: e.clientX, y: e.clientY }, getItems(), title);
    },
    onTouchStart: (e: React.TouchEvent) => {
      const t = e.touches[0]!;
      start.current = { x: t.clientX, y: t.clientY };
      timer.current = setTimeout(() => {
        timer.current = null;
        // The hold picked the row up for dragging instead.
        if (isDragging()) return;
        navigator.vibrate?.(10);
        openMenu({ x: t.clientX, y: t.clientY }, getItems(), title);
      }, 480);
    },
    onTouchMove: (e: React.TouchEvent) => {
      const t = e.touches[0]!;
      if (
        start.current &&
        Math.hypot(t.clientX - start.current.x, t.clientY - start.current.y) > 10 &&
        timer.current
      ) {
        clearTimeout(timer.current);
        timer.current = null;
      }
    },
    onTouchEnd: () => {
      if (timer.current) clearTimeout(timer.current);
      timer.current = null;
    },
  };
}
